import React, { useMemo } from 'react';

import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { useConfigValue } from '~/shared/utils/config.js';
import { useBreakpoint } from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl/index.js';
import { useDomain } from '~/techstyle-shared/redux-core';

import { getColorSwatches } from '../../../grid/utils/productFilters.js';
import useUnifiedProductGridData from '../../hooks/useUnifiedProductGridData';
import {
  isFacetOptionSelected,
  updateFiltersInUrlParams,
  sanitizeFilterName,
  getSelectedFacetRbid,
  getTranslatedFacetOptionDisplayName,
  isImageSwatchFacet,
  generateFilterImageUrl,
} from '../../utils/filters';
import { scrollToTopOfGrid } from '../../utils/scroll.js';
import ImageSwatch from '../SharedGridFiltersUi/ImageSwatch.js';

const DynamicFormattedMessage = FormattedMessage;

const DesktopGridColorFilterFieldset = styled.fieldset`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  border: none;
`;

const HiddenLegend = styled.legend`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const HiddenCheckbox = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 3px;
  border-radius: 9999px;
  cursor: pointer;
  ${({ theme }) => theme.gridFilterStyles.colorFilterStyle};

  ${({ isSelected }) =>
    isSelected
      ? css`
          background-color: black;
          color: white;
          border: 1px solid white;
        `
      : css`
          background-color: ${({ theme }) =>
            theme.colors.gridSelectedFiltersPillBackgroundColor};
          color: ${({ theme }) => theme.colors.palette.shadow90};
        `}
`;

const ColorSwatch = styled.span`
  width: 22px;
  height: 22px;
  border-radius: 9999px;
  border: 1px solid
    ${({ hasColor, theme }) =>
      hasColor ? 'black' : theme.colors.gridSelectedFiltersPillBackgroundColor};
  background-color: ${({ color }) => color || 'none'};
`;

function DesktopGridColorFilter({ options, name }) {
  const { selectedUrlFilters } = useUnifiedProductGridData();
  const sanitizedName = sanitizeFilterName(name);
  const router = useRouter();
  const { isMobile } = useBreakpoint();
  const domain = useDomain();
  const colorSwatches = useMemo(() => getColorSwatches(), []);
  const imageSwatchFilters = useConfigValue('public.imageSwatchFilters');
  const filterImageCdnUrl = useConfigValue('public.filterImageCdnUrl');

  const handleOnChange = ({ facetName, facetOption }) => {
    updateFiltersInUrlParams({
      facetName,
      facetOption,
      router,
    });
    scrollToTopOfGrid({ isMobile });
  };

  return (
    <DesktopGridColorFilterFieldset aria-labelledby={`${name}-legend`}>
      <HiddenLegend id={`${name}-legend`}>{name}</HiddenLegend>

      {options.map((option) => {
        const inputId = `${name}-${option.value}`;
        const isSelected = isFacetOptionSelected({
          facetName: name,
          facetOption: option.value,
          selectedUrlFilters,
        });

        const swatchColor = colorSwatches[option.value];

        const facetOptionDisplayName = getTranslatedFacetOptionDisplayName({
          facetName: name,
          facetOption: option,
          domain,
        });

        const shouldUseImageSwatch = isImageSwatchFacet({
          facetName: name,
          optionValue: option.value,
          imageSwatchFilters,
        });
        const imageUrl = shouldUseImageSwatch
          ? generateFilterImageUrl(name, option.value, filterImageCdnUrl)
          : null;

        return (
          <React.Fragment key={option.value}>
            <HiddenCheckbox
              id={inputId}
              type="checkbox"
              name={name}
              checked={isSelected}
              onChange={() =>
                handleOnChange({
                  facetName: name,
                  facetOption: option.value,
                })
              }
            />

            <Label
              htmlFor={inputId}
              isSelected={isSelected}
              role="checkbox"
              aria-checked={isSelected}
              tabIndex={0}
              data-autotag={`filter-${sanitizedName}-option`}
              onKeyDown={(e) => {
                if (e.key === ' ' || e.key === 'Enter') {
                  e.preventDefault();
                  const el = document.getElementById(inputId);
                  if (el) {
                    el.click();
                  }
                }
              }}
            >
              {shouldUseImageSwatch ? (
                <ImageSwatch imageUrl={imageUrl} aria-hidden="true" />
              ) : (
                <ColorSwatch
                  color={swatchColor}
                  hasColor={!!swatchColor}
                  aria-hidden="true"
                />
              )}
              <DynamicFormattedMessage
                id={`site_product_filter.${getSelectedFacetRbid(
                  name,
                  facetOptionDisplayName
                )}`}
                defaultMessage={facetOptionDisplayName}
              />
            </Label>
          </React.Fragment>
        );
      })}
    </DesktopGridColorFilterFieldset>
  );
}

DesktopGridColorFilter.propTypes = {
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      display_name: PropTypes.string.isRequired,
    })
  ).isRequired,
};

export default DesktopGridColorFilter;
